/// Port of mobile/lib/ui/pixel/pixel_theme_picker.dart.
///
/// Two-way control with a sliding inverted selection block behind the labels.

import type { AppTheme } from '@/settings/data/settingsLocalData';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PixelContainer } from './PixelContainer';

const animDurationMs = 180;
const options: readonly AppTheme[] = ['light', 'dark'];

export interface PixelThemePickerProps {
  value: AppTheme;
  labelOf: (value: AppTheme) => string;
  onChange: (value: AppTheme) => void;
  pixelScale?: number;
  radius?: number;
}

export function PixelThemePicker({
  value,
  labelOf,
  onChange,
  pixelScale = 2,
  radius = 3,
}: PixelThemePickerProps) {
  const colors = useThemeColors();
  const selectedIndex = Math.max(0, options.indexOf(value));
  const slotPercent = 100 / options.length;

  return (
    <PixelContainer
      radius={radius}
      pixelScale={pixelScale}
      padding="4px"
      backgroundColor={colors.surfaceContainerHighest}
      borderColor={colors.onSurface}
      className="w-full"
    >
      <div className="relative" style={{ height: 40 }}>
        <div
          className="pointer-events-none absolute inset-y-0 p-[2px]"
          style={{
            width: `${slotPercent}%`,
            left: `${selectedIndex * slotPercent}%`,
            transition: `left ${animDurationMs}ms ease-out`,
          }}
        >
          <PixelContainer
            radius={2}
            pixelScale={pixelScale}
            padding="0px"
            backgroundColor={colors.onSurface}
            borderColor={colors.onSurface}
            className="h-full w-full"
          >
            <span className="block h-full w-full" />
          </PixelContainer>
        </div>

        <div className="relative flex h-full">
          {options.map((option) => {
            const selected = option === value;
            const ink = selected ? colors.surfaceContainerHighest : colors.onSurface;

            return (
              <button
                key={option}
                type="button"
                aria-pressed={selected}
                onClick={() => onChange(option)}
                className="flex flex-1 items-center justify-center truncate leading-none"
                style={{
                  fontSize: 20,
                  color: ink,
                  opacity: selected ? 1 : 0.55,
                  transition: `color ${animDurationMs}ms, opacity ${animDurationMs}ms`,
                }}
              >
                {labelOf(option)}
              </button>
            );
          })}
        </div>
      </div>
    </PixelContainer>
  );
}
